/**
 * STOQ preorders arrive as Scheduled (selling plan + Shipping/Preorder properties).
 * Convert to Unfulfilled at cart level (remove selling plan) for split fulfillment,
 * while preserving STOQ ship-date properties for the delivery date picker.
 */
(function () {
  'use strict';

  var BACKUP_KEY = 'cc_preorder_props_backup';
  var MONTH_MAP = { jan:0, feb:1, mar:2, apr:3, may:4, jun:5, jul:6, aug:7, sep:8, oct:9, nov:10, dec:11 };
  var RETRY_DELAYS_MS = [0, 400, 1200];
  var PREPARE_FETCH_DELAYS_MS = [0, 200, 500, 1000];
  // STOQ preorder selling plan ID — the plan that creates the "Scheduled" fulfillment group
  // causing double shipping when combined with ReCharge subscriptions.
  var STOQ_PREORDER_PLAN_ID = 711980777850;

  // ── STOQ re-attach blocker ──────────────────────────────────────────────────
  // STOQ re-attaches the preorder selling plan after every /cart/change.js call it
  // observes. We intercept window.fetch and return a fake success response when
  // STOQ tries to re-attach plan 711980777850, so the server-side cart stays clean
  // (no selling plan → no Scheduled fulfillment group → no double shipping).
  var _origFetch = window.fetch;

  function _buildFakeReattachResponse(requestBody) {
    var itemId, planId;
    try {
      var parsed = JSON.parse(requestBody || '{}');
      itemId = String(parsed.id || '');
      planId = parseInt(parsed.selling_plan || STOQ_PREORDER_PLAN_ID, 10);
    } catch (e) {
      try {
        var ps = new URLSearchParams(requestBody || '');
        itemId = ps.get('id') || '';
        planId = parseInt(ps.get('selling_plan') || String(STOQ_PREORDER_PLAN_ID), 10);
      } catch (e2) {
        planId = STOQ_PREORDER_PLAN_ID;
      }
    }
    // Fetch the live (stripped) cart and inject a fake selling_plan_allocation so
    // STOQ believes its re-attach succeeded. The server cart is unchanged.
    return _origFetch.call(window, '/cart.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        var fakeCart = cart ? JSON.parse(JSON.stringify(cart)) : { items: [] };
        fakeCart.items.forEach(function (item) {
          // Match by variant_id (item key changes after strip, but variant_id is stable)
          if (item.key === itemId || String(item.variant_id) === itemId ||
              (item.properties && (item.properties._cc_preorder_shipping || item.properties.Shipping))) {
            if (!item.selling_plan_allocation) {
              item.selling_plan_allocation = {
                price_adjustments: [],
                price: item.price,
                compare_at_price: item.compare_at_price,
                per_delivery_price: item.price,
                selling_plan: {
                  id: planId,
                  name: 'Preorder',
                  description: null,
                  options: [{ name: 'Preorder', position: 1, value: 'Full payment' }],
                  recurring_deliveries: false,
                  fixed_selling_plan: true,
                  price_adjustments: []
                }
              };
            }
          }
        });
        return new Response(JSON.stringify(fakeCart), {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        });
      });
  }

  window.fetch = function ccFetchProxy(input, init) {
    var url = typeof input === 'string' ? input
      : (input && typeof input.url === 'string') ? input.url : '';
    if (url.indexOf('cart/change') !== -1) {
      var body = '';
      if (init && init.body) {
        try {
          body = typeof init.body === 'string' ? init.body : new URLSearchParams(init.body).toString();
        } catch (e) {
          try { body = String(init.body); } catch (e2) {}
        }
      }
      if (body.indexOf(String(STOQ_PREORDER_PLAN_ID)) !== -1) {
        console.log('[cc-normalize] FETCH PROXY: blocked STOQ preorder plan reattach — returning fake success');
        return _buildFakeReattachResponse(body);
      }
    }
    return _origFetch.apply(this, arguments);
  };
  // ── end STOQ re-attach blocker ──────────────────────────────────────────────

  var state = {
    busy: false,
    rerunPending: false,
    debounceTimer: null,
    retryTimers: [],
    burstId: 0,
    restoreAttempts: {},
    suppressAutoUntil: 0,
    normalizePromise: null  // in-flight promise so the checkout gate can wait for it
  };

  function cartRoot() {
    return (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
  }

  function fetchCartJson() {
    if (typeof window.ccGetCartSnapshot === 'function') {
      return window.ccGetCartSnapshot({ forceFetch: true, maxAgeMs: 0 });
    }
    return fetch(cartRoot() + '.js').then(function (r) { return r.ok ? r.json() : null; });
  }

  function postCartChange(body) {
    return fetch(cartRoot() + '/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.ok ? r.json() : null;
    });
  }

  function publishCart(cart, source) {
    if (!cart) return;
    if (typeof window.ccDispatchCartChange === 'function') {
      window.ccDispatchCartChange({ cart: cart, cartUpdated: true, source: source || 'cc-preorder-unfulfill' });
    } else if (typeof window.ccPublishCartSnapshot === 'function') {
      window.ccPublishCartSnapshot(cart, source || 'cc-preorder-unfulfill');
      document.dispatchEvent(new CustomEvent('on:cart:change', {
        bubbles: true,
        detail: { cart: cart, cartUpdated: true, source: source || 'cc-preorder-unfulfill' }
      }));
    }
  }

  function refreshCartItemsDom() {
    var cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refreshCartItems === 'function') {
      return cartItems.refreshCartItems();
    }
    return Promise.resolve();
  }

  function isAutoNormalizeSuppressed() {
    return Date.now() < state.suppressAutoUntil;
  }

  function isPreorderSellingPlan(item) {
    var plan = item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    if (!plan || !plan.name) return false;
    var name = String(plan.name).toLowerCase();
    return name.indexOf('preorder') !== -1 || name.indexOf('pre-order') !== -1 || name.indexOf('pre order') !== -1;
  }

  function hasVisibleStoqPreorderProperties(props) {
    props = props || {};
    if (props.Shipping || props.Preorder) return true;
    return Object.keys(props).some(function (key) {
      var val = props[key];
      return parseShipDateFromText(key) || parseShipDateFromText(val);
    });
  }

  function parseShipDateFromText(str) {
    var text = String(str || '');
    var shipsOn = text.match(/ships\s+on\s+([A-Za-z]+)\.?\s+(\d{1,2}),?\s*(\d{4})/i);
    if (shipsOn) {
      var mo1 = MONTH_MAP[shipsOn[1].toLowerCase().slice(0, 3)];
      if (mo1 !== undefined) {
        var d1 = new Date(parseInt(shipsOn[3], 10), mo1, parseInt(shipsOn[2], 10));
        if (!isNaN(d1.getTime())) return d1;
      }
    }
    var shortDate = text.match(/([A-Za-z]+)\.?\s+(\d{1,2}),?\s+(\d{4})/);
    if (shortDate) {
      var mo2 = MONTH_MAP[shortDate[1].toLowerCase().slice(0, 3)];
      if (mo2 !== undefined) {
        var d2 = new Date(parseInt(shortDate[3], 10), mo2, parseInt(shortDate[2], 10));
        if (!isNaN(d2.getTime())) return d2;
      }
    }
    return null;
  }

  function cloneProperties(props) {
    var out = {};
    Object.keys(props || {}).forEach(function (key) {
      var val = props[key];
      if (val != null && String(val).trim() !== '') out[key] = String(val);
    });
    return out;
  }

  function buildRestoredProperties(props) {
    var restored = cloneProperties(props);
    if (restored.Shipping && !restored._cc_preorder_shipping) {
      restored._cc_preorder_shipping = restored.Shipping;
    }
    if (restored.Preorder && !restored._cc_preorder_detail) {
      restored._cc_preorder_detail = restored.Preorder;
    }
    restored._cc_preorder_normalized = 'true';
    return restored;
  }

  function readBackupStore() {
    try {
      return JSON.parse(sessionStorage.getItem(BACKUP_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function writeBackupStore(store) {
    try {
      sessionStorage.setItem(BACKUP_KEY, JSON.stringify(store || {}));
    } catch (e) {}
  }

  function backupProperties(lineKey, variantId, props) {
    var store = readBackupStore();
    var payload = {
      lineKey: lineKey,
      variantId: String(variantId),
      properties: buildRestoredProperties(props),
      savedAt: Date.now()
    };
    // Keyed by variantId only — line keys change when selling plan is stripped,
    // so using them as the primary key would create stale duplicates.
    // v: prefix distinguishes from line-key entries written by older versions.
    store['v:' + String(variantId)] = payload;
    writeBackupStore(store);
    console.log('[cc-normalize] backupProperties stored for variant', variantId, '| store size:', Object.keys(store).length);
    return payload.properties;
  }

  function getBackupForItem(item) {
    var store = readBackupStore();
    // Prefer stable v:variantId key; fall back to legacy lineKey entry
    if (item && item.variant_id && store['v:' + String(item.variant_id)]) {
      return store['v:' + String(item.variant_id)].properties;
    }
    if (item && item.key && store[item.key]) return store[item.key].properties;
    return null;
  }

  function pruneBackupStore(cart) {
    var store = readBackupStore();
    var keys = Object.keys(store);
    if (!keys.length) return;

    // Build set of variant IDs currently in cart
    var liveVariantIds = {};
    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach(function (item) {
        liveVariantIds[String(item.variant_id)] = true;
      });
    }
    // If cart is empty, liveVariantIds is empty — all entries will be pruned

    var pruned = [];
    keys.forEach(function (key) {
      var entry = store[key];
      var variantId = entry && String(entry.variantId);
      if (!variantId || !liveVariantIds[variantId]) {
        delete store[key];
        pruned.push(key);
      }
    });

    if (pruned.length) {
      writeBackupStore(store);
      console.log('[cc-normalize] pruneBackupStore: removed', pruned.length, 'stale entries:', pruned, '| remaining:', Object.keys(store).length);
    }
  }

  function propertiesNeedRestore(item) {
    var props = item.properties || {};
    if (hasVisibleStoqPreorderProperties(props)) return false;
    var backup = getBackupForItem(item);
    return !!(backup && hasVisibleStoqPreorderProperties(backup));
  }

  function isScheduledPreorderLine(item) {
    return !!(item && isPreorderSellingPlan(item) && hasVisibleStoqPreorderProperties(item.properties));
  }

  function cartNeedsPreorderWork(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some(function (item) {
      return isScheduledPreorderLine(item) || propertiesNeedRestore(item);
    });
  }

  function findLineByVariant(cart, variantId, quantity) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return cart.items.find(function (item) {
      return String(item.variant_id) === String(variantId)
        && Number(item.quantity) === Number(quantity);
    }) || null;
  }

  function restorePropertiesOnLine(item, properties) {
    if (!item || !item.key || !properties) return Promise.resolve(null);
    var attemptKey = String(item.key) + ':' + String(item.variant_id);
    if ((state.restoreAttempts[attemptKey] || 0) >= 2) return Promise.resolve(null);
    state.restoreAttempts[attemptKey] = (state.restoreAttempts[attemptKey] || 0) + 1;
    return postCartChange({
      id: item.key,
      quantity: item.quantity,
      properties: properties
    });
  }

  function normalizeScheduledLine(item) {
    var planName = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.name;
    console.log('[cc-normalize] STRIPPING selling plan from item:', item.key, '| plan:', planName, '| properties:', JSON.stringify(item.properties));
    var savedProps = backupProperties(item.key, item.variant_id, item.properties);
    console.log('[cc-normalize] backed up properties:', JSON.stringify(savedProps));
    return postCartChange({
      id: item.key,
      quantity: item.quantity,
      selling_plan: null,
      properties: savedProps
    }).then(function (cart) {
      if (!cart) { console.warn('[cc-normalize] cart/change returned null for item', item.key); return null; }

      var line = cart.items.find(function (it) { return it.key === item.key; })
        || findLineByVariant(cart, item.variant_id, item.quantity);

      if (!line) { console.warn('[cc-normalize] could not find line after change for item', item.key); return cart; }

      var linePlanName = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan && line.selling_plan_allocation.selling_plan.name;
      console.log('[cc-normalize] after strip — item key:', line.key, '| selling_plan:', linePlanName || 'NONE', '| properties:', JSON.stringify(line.properties));

      if (!hasVisibleStoqPreorderProperties(line.properties)) {
        console.log('[cc-normalize] properties lost after strip — restoring saved props to item', line.key);
        return restorePropertiesOnLine(line, savedProps).then(function (cart2) {
          if (cart2) {
            var restored = cart2.items.find(function (it) { return it.key === line.key; });
            console.log('[cc-normalize] after restore — properties:', restored ? JSON.stringify(restored.properties) : 'item not found');
          }
          return cart2 || cart;
        });
      }
      console.log('[cc-normalize] properties intact after strip — no restore needed');
      return cart;
    });
  }

  function processCart(cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
      return Promise.resolve({ cart: cart, changed: false });
    }

    console.log('[cc-normalize] processCart: scanning', cart.items.length, 'item(s)');
    cart.items.forEach(function (item) {
      var planName = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.name;
      console.log('[cc-normalize]   item:', item.key, '| selling_plan:', planName || 'none', '| isScheduled:', isScheduledPreorderLine(item), '| needsRestore:', propertiesNeedRestore(item), '| props:', JSON.stringify(item.properties));
    });

    var chain = Promise.resolve(cart);
    var changed = false;

    cart.items.forEach(function (item) {
      if (isScheduledPreorderLine(item)) {
        changed = true;
        chain = chain.then(function (currentCart) {
          var target = currentCart.items.find(function (it) { return it.key === item.key; }) || item;
          return normalizeScheduledLine(target);
        });
        return;
      }

      if (propertiesNeedRestore(item)) {
        changed = true;
        var backup = getBackupForItem(item);
        console.log('[cc-normalize] item', item.key, 'needs property restore — backup:', JSON.stringify(backup));
        chain = chain.then(function () {
          return restorePropertiesOnLine(item, backup);
        });
      }
    });

    return chain.then(function (finalCart) {
      if (finalCart) {
        console.log('[cc-normalize] processCart done | changed:', changed);
        return { cart: finalCart, changed: changed };
      }
      // A strip failed (stale key / 400) — re-fetch real cart so we don't publish stale state
      console.log('[cc-normalize] processCart: strip failed, re-fetching fresh cart');
      return fetchCartJson().then(function (freshCart) {
        console.log('[cc-normalize] processCart done (fresh re-fetch) | changed:', changed);
        return { cart: freshCart || cart, changed: changed };
      });
    });
  }

  function fetchFreshSections(sectionKeys) {
    if (!sectionKeys || !sectionKeys.length) return Promise.resolve(null);
    var url = window.location.pathname + '?sections=' + sectionKeys.join(',');
    return fetch(url).then(function (r) { return r.ok ? r.json() : null; });
  }

  function waitForCartReady(addData) {
    var addedVariantId = addData && addData.variant_id;
    var chain = Promise.resolve(null);

    PREPARE_FETCH_DELAYS_MS.forEach(function (delay) {
      chain = chain.then(function (cart) {
        if (cart && (!addedVariantId || cartNeedsPreorderWork(cart) || cart.items.some(function (item) {
          return String(item.variant_id) === String(addedVariantId);
        }))) {
          return cart;
        }
        return new Promise(function (resolve) {
          setTimeout(function () {
            fetchCartJson().then(resolve);
          }, delay);
        });
      });
    });

    return chain.then(function (cart) {
      return cart || fetchCartJson();
    });
  }

  function normalizePreorderCart(opts) {
    opts = opts || {};
    var refreshDom = opts.refreshDom !== false;
    var publish = opts.publish !== false;
    var source = opts.source || 'cc-preorder-unfulfill';

    if (state.busy) {
      state.rerunPending = true;
      console.log('[cc-normalize] normalizePreorderCart skipped — already busy');
      return Promise.resolve({ cart: null, changed: false });
    }

    console.log('[cc-normalize] normalizePreorderCart START | source:', source, '| refreshDom:', refreshDom, '| publish:', publish);
    state.busy = true;
    state.normalizePromise = fetchCartJson()
      .then(function (cart) {
        pruneBackupStore(cart);
        return cart;
      })
      .then(processCart)
      .then(function (result) {
        if (!result || !result.changed || !result.cart) {
          console.log('[cc-normalize] normalizePreorderCart END — no changes needed');
          return result;
        }

        console.log('[cc-normalize] normalizePreorderCart END — cart changed, publishing | publish:', publish);
        if (publish) {
          publishCart(result.cart, source);
        } else if (window.ccPublishCartSnapshot) {
          // Keep cache warm without dispatching on:cart:change (avoids triggering a drawer re-render)
          window.ccPublishCartSnapshot(result.cart, source);
        }

        if (refreshDom) {
          return refreshCartItemsDom().then(function () {
            return result;
          });
        }
        return result;
      })
      .catch(function (err) { console.error('[cc-normalize] normalizePreorderCart ERROR:', err); return { cart: null, changed: false }; })
      .finally(function () {
        state.busy = false;
        state.normalizePromise = null;
        if (state.rerunPending) {
          state.rerunPending = false;
          console.log('[cc-normalize] rerunPending — running normalize again');
          normalizePreorderCart(opts);
        }
      });
    return state.normalizePromise;
  }

  /**
   * Run before the first cart drawer render after ATC.
   * Normalizes preorder lines via Cart API, refetches sections if needed,
   * and suppresses the background auto-normalize refresh for one render cycle.
   */
  function prepareCartBeforeRender(addData) {
    if (!addData) return Promise.resolve(addData);

    state.suppressAutoUntil = Date.now() + 6000;
    clearRetryTimers();
    clearTimeout(state.debounceTimer);

    var sectionKeys = addData.sections ? Object.keys(addData.sections) : [];

    return waitForCartReady(addData)
      .then(processCart)
      .then(function (result) {
        if (!result || !result.cart) return addData;

        if (window.ccPublishCartSnapshot) {
          window.ccPublishCartSnapshot(result.cart, 'cc-preorder-prepare');
        }

        if (!result.changed) {
          return Object.assign({}, addData, {
            item_count: result.cart.item_count,
            total_price: result.cart.total_price
          });
        }

        return fetchFreshSections(sectionKeys).then(function (sections) {
          return Object.assign({}, addData, {
            item_count: result.cart.item_count,
            total_price: result.cart.total_price,
            sections: sections || addData.sections
          });
        });
      })
      .catch(function () {
        return addData;
      });
  }

  function clearRetryTimers() {
    state.retryTimers.forEach(function (timerId) {
      clearTimeout(timerId);
    });
    state.retryTimers = [];
  }

  function scheduleNormalizePreorderLines() {
    if (isAutoNormalizeSuppressed()) return;

    clearTimeout(state.debounceTimer);
    state.debounceTimer = setTimeout(function () {
      if (isAutoNormalizeSuppressed()) return;

      state.burstId += 1;
      var burst = state.burstId;
      clearRetryTimers();
      RETRY_DELAYS_MS.forEach(function (delay) {
        var timerId = setTimeout(function () {
          if (burst !== state.burstId || isAutoNormalizeSuppressed()) return;
          normalizePreorderCart({ refreshDom: false, publish: false });
        }, delay);
        state.retryTimers.push(timerId);
      });
    }, 60);
  }

  function shouldReactToCartEvent(detail) {
    if (!detail) return true;
    var source = detail.source || '';
    if (source === 'cc-preorder-unfulfill' || source === 'cc-preorder-prepare') return false;
    if (source === 'product-form-atc' || source === 'quick-add-atc') return false;
    if (source === 'refreshCartItems') return false;
    return true;
  }

  window.ccParsePreorderShipDateFromText = parseShipDateFromText;

  window.ccParsePreorderShipDateFromProperties = function (props) {
    props = props || {};
    var latest = null;
    var candidates = [
      props.Shipping,
      props.Preorder,
      props._cc_preorder_shipping,
      props._cc_preorder_detail
    ];
    Object.keys(props).forEach(function (key) {
      candidates.push(key, props[key]);
    });
    candidates.forEach(function (text) {
      var d = parseShipDateFromText(text);
      if (d && (!latest || d > latest)) latest = d;
    });
    return latest;
  };

  window.ccNormalizePreorderLines = function () {
    return normalizePreorderCart({ refreshDom: true, publish: true });
  };
  window.ccPrepareCartBeforeRender = prepareCartBeforeRender;
  window.ccScheduleNormalizePreorderLines = scheduleNormalizePreorderLines;

  document.addEventListener('on:cart:change', function (e) {
    var detail = e && e.detail;
    if (!shouldReactToCartEvent(detail)) return;
    console.log('[cc-normalize] on:cart:change triggered normalize | source:', detail && detail.source);
    scheduleNormalizePreorderLines();
  });

  document.addEventListener('on:line-item:change', function (e) {
    var detail = e && e.detail;
    if (!shouldReactToCartEvent(detail)) return;
    console.log('[cc-normalize] on:line-item:change triggered normalize | source:', detail && detail.source);
    scheduleNormalizePreorderLines();
  });

  document.addEventListener('dispatch:cart-drawer:refresh', function () {
    console.log('[cc-normalize] dispatch:cart-drawer:refresh triggered normalize');
    scheduleNormalizePreorderLines();
  });

  // Log cart state on page load so we can verify it's clean after returning from checkout
  (function logCartOnLoad() {
    function logCartState() {
      fetch('/cart.js').then(function (r) { return r.ok ? r.json() : null; }).then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        console.log('[cc-normalize] PAGE LOAD cart state | items:', cart.item_count);
        cart.items.forEach(function (item) {
          var planName = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.name;
          console.log('[cc-normalize]   item:', item.key, '| selling_plan:', planName || 'NONE', '| _cc_preorder_normalized:', item.properties && item.properties._cc_preorder_normalized, '| props:', JSON.stringify(item.properties));
        });
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', logCartState);
    } else {
      logCartState();
    }
  }());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleNormalizePreorderLines);
  } else {
    scheduleNormalizePreorderLines();
  }

  // ── Checkout gate ────────────────────────────────────────────────────────────
  // Block the cart form submit until any preorder selling plan has been removed.
  // Without this gate the Preorder selling plan can still be on the line at the
  // moment Shopify builds the checkout session, which puts the preorder into a
  // separate fulfillment group from subscriptions and charges shipping twice.
  (function () {
    var CHECKOUT_FORM_IDS = { 'cart-drawer-form': true, 'cart-form': true };

    function fetchCartFresh() {
      // Force a real fetch — the TTL cache can return stale clean data after STOQ
      // silently re-attaches the selling plan, so we must always check the live cart.
      if (typeof window.ccGetCartSnapshot === 'function') {
        return window.ccGetCartSnapshot({ forceFetch: true, maxAgeMs: 0 });
      }
      return fetch(cartRoot() + '.js').then(function (r) { return r.ok ? r.json() : null; });
    }

    function resubmit(formId) {
      var form = document.getElementById(formId);
      if (!form) { console.log('[cc-gate] resubmit: form not found', formId); return; }
      var checkoutUrl = '/checkout';
      console.log('[cc-gate] NAVIGATING TO CHECKOUT at', new Date().toISOString(), '|', checkoutUrl);
      // Navigate directly — bypasses any third-party form submit listeners (e.g. STOQ)
      // that intercept requestSubmit and redirect away from checkout.
      window.location.href = checkoutUrl;
    }

    function setCheckoutLoading(formId, loading) {
      var btn = document.querySelector('[name="checkout"][form="' + formId + '"]');
      if (!btn) return;
      if (loading) {
        btn.classList.add('cc-checkout-loading');
      } else {
        btn.classList.remove('cc-checkout-loading');
      }
    }

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !CHECKOUT_FORM_IDS[form.id]) return;
      if (e.defaultPrevented) { console.log('[cc-gate] submit blocked by another listener (e.defaultPrevented)'); return; }
      if (form._ccGateInFlight) { console.log('[cc-gate] ignored — already processing first click'); return; }

      e.preventDefault();

      var formId = form.id;
      var clickedAt = Date.now();
      form._ccGateInFlight = true;
      setCheckoutLoading(formId, true);
      console.log('[cc-gate] BUTTON CLICKED at', new Date(clickedAt).toISOString(), '|', formId, '| normalizeInFlight:', !!state.normalizePromise, '| stateBusy:', state.busy);

      var safetyTimer = setTimeout(function () {
        console.warn('[cc-gate] STUCK — safety timer fired after', Date.now() - clickedAt, 'ms — resetting gate');
        setCheckoutLoading(formId, false);
        var f = document.getElementById(formId);
        if (f) f._ccGateInFlight = false;
      }, 5000);

      function clearGateState() {
        clearTimeout(safetyTimer);
        setCheckoutLoading(formId, false);
        var f = document.getElementById(formId);
        if (f) f._ccGateInFlight = false;
      }

      var waitForInFlight = state.normalizePromise
        ? state.normalizePromise.catch(function () {})
        : Promise.resolve();

      waitForInFlight
        .then(fetchCartFresh)
        .then(function (cart) {
          var preorderLines = cart && Array.isArray(cart.items)
            ? cart.items.filter(isScheduledPreorderLine)
            : [];
          console.log('[cc-gate] cart fetched at +' + (Date.now() - clickedAt) + 'ms | items:', cart && cart.item_count, '| preorder lines with plan:', preorderLines.length);

          if (!cart || !Array.isArray(cart.items) || !preorderLines.length) {
            console.log('[cc-gate] cart clean → resubmit at +' + (Date.now() - clickedAt) + 'ms');
            resubmit(formId);
            return;
          }

          console.log('[cc-gate] preorder selling plan found — normalizing at +' + (Date.now() - clickedAt) + 'ms');
          return normalizePreorderCart({ refreshDom: false, publish: false })
            .then(function () {
              console.log('[cc-gate] normalize done → resubmit at +' + (Date.now() - clickedAt) + 'ms');
              resubmit(formId);
            });
        })
        .catch(function (err) {
          console.error('[cc-gate] error in gate chain at +' + (Date.now() - clickedAt) + 'ms:', err);
          clearGateState();
          resubmit(formId);
        });
    });
  }());
})();
